import { useState } from "react";
import { prefetchConferenceHub } from "@/lib/conferencePrefetch";

import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ofuesiLogo from "../assets/ofuesi-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();

  const primaryItems = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Conference", href: "/conference" },
    { name: "Prayer", href: "/prayer" },
    { name: "Regions", href: "/regions" },
    { name: "Give", href: "/give" },
    { name: "Women's Ministry", href: "/womens-ministry" },
  ];

  const moreItems = [
    { name: "Keep in Touch", href: "/keep-in-touch" },
    { name: "Gallery", href: "/gallery" },
    { name: "Contact", href: "/contact" },
    { name: "Admin", href: "/admin" },
  ];

  const isActive = (href: string) => location.pathname === href;

  const handleLogoError = () => {
    setLogoError(true);
  };

  return (
    <motion.nav
      className="sticky top-0 z-50 w-full border-b backdrop-blur supports-[backdrop-filter]:bg-ministry-blue/90 shadow-divine"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        background: "linear-gradient(90deg, hsl(214, 76%, 19%), hsl(214, 76%, 25%))",
        minHeight: "90px",
      }}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex min-h-20 md:h-20 items-center gap-3 py-2 md:py-0">
          {/* Enhanced Logo Section */}
          <motion.div
            className="min-w-0 flex-1 md:flex-none md:flex-shrink-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link to="/" className="flex items-center space-x-3 sm:space-x-4 group min-w-0">
              <div className="relative flex-shrink-0">
                {!logoError ? (
                  <motion.img
                    src={ofuesiLogo}
                    alt="OFUESI Logo"
                    className="h-16 w-16 sm:h-20 sm:w-20 xl:h-28 xl:w-28 object-contain flex-shrink-0"
                    onError={handleLogoError}
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  />
                ) : (
                  <motion.div
                    className="h-28 w-28 rounded-full bg-gradient-to-br from-ministry-blue via-ministry-deep to-ministry-blue flex items-center justify-center text-white shadow-lg border-2 border-ministry-gold"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="text-center">
                      <div className="text-[11px] font-black leading-tight">OVERSEAS</div>
                      <div className="text-[11px] font-black leading-tight">FRIENDS</div>
                      <div className="text-[11px] font-black leading-tight">OF UESI</div>
                    </div>
                  </motion.div>
                )}
              </div>
              <div className="min-w-0">
                <motion.div
                  className="text-2xl sm:text-3xl font-black tracking-wide text-white leading-none"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  OFUESI
                </motion.div>
                <motion.div
                  className="mt-1"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <p
                    className="text-white/[0.82] font-normal break-words text-[11px] sm:text-[12.5px] xl:text-[14px] md:max-w-[250px] lg:max-w-[330px] xl:max-w-none 2xl:whitespace-nowrap"
                    style={{ letterSpacing: "0.2px", lineHeight: 1.35 }}
                  >
                    {[
                      ["O", "verseas"],
                      ["F", "riends"],
                      ["", "of"],
                      ["U", "nion"],
                      ["", "of"],
                      ["E", "vangelical"],
                      ["S", "tudents"],
                      ["", "of"],
                      ["I", "ndia"],
                    ].map(([initial, rest], i) => (
                      <span key={i}>
                        <span className="whitespace-nowrap">
                          {initial && (
                            <span
                              className="text-ministry-gold align-baseline"
                              style={{
                                fontSize: "1.12em",
                                fontWeight: 600,
                                letterSpacing: "0.2px",
                                WebkitFontSmoothing: "antialiased",
                              }}
                            >
                              {initial}
                            </span>
                          )}
                          {rest}
                        </span>
                        {i < 8 ? " " : ""}
                      </span>
                    ))}
                  </p>
                </motion.div>
              </div>
            </Link>
          </motion.div>

          {/* Flexible spacer keeps navigation clear of the branding block */}
          <div className="hidden md:block flex-1 min-w-[1.5rem]" aria-hidden="true" />

          {/* Desktop Navigation with Parallelogram Design */}
          <motion.div
            className="hidden xl:flex items-center space-x-0.5 2xl:space-x-2 flex-shrink-0 flex-nowrap"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {primaryItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                className="relative flex-shrink-0"
              >
                <Link
                  to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`
                    relative inline-block whitespace-nowrap px-3 2xl:px-5 py-3 text-[13px] 2xl:text-sm font-semibold transition-all duration-300
                    before:content-[''] before:absolute before:inset-0 before:bg-white/10 before:backdrop-blur-sm
                    before:border before:border-white/20 before:transform before:skew-x-12
                    hover:before:bg-white/20 hover:before:border-white/30
                    ${
                      isActive(item.href)
                        ? "text-ministry-gold before:bg-ministry-gold/20 before:border-ministry-gold/30"
                        : "text-white"
                    }
                  `}
                  style={{
                    clipPath: "polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)",
                  }}
                >
                  <span className="relative z-10">{item.name}</span>
                </Link>
                {/* Slanting line divider */}
                {index < primaryItems.length - 1 && (
                  <div
                    className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-px h-8 bg-white/30"
                    style={{ transform: "translateY(-50%) skewX(-12deg)" }}
                  />
                )}
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="ml-2 2xl:ml-4 flex-shrink-0"
            >
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-ministry-gold text-ministry-gold hover:bg-ministry-gold hover:text-ministry-blue"
                  >
                    More
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-64 bg-ministry-blue border-ministry-gold/20">
                  <div className="flex flex-col space-y-4 mt-8">
                    {moreItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                        onClick={() => {
                          setIsOpen(false);
                          window.scrollTo(0, 0);
                        }}
                        className={`text-sm font-medium transition-colors hover:text-ministry-gold ${
                          isActive(item.href) ? "text-ministry-gold" : "text-white"
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </motion.div>
          </motion.div>

          {/* Large Screen Navigation (L to XL) - iPad View */}
          <motion.div
            className="hidden md:flex xl:hidden items-center space-x-1 flex-shrink-0 flex-nowrap"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {primaryItems.slice(0, 3).map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                className="relative"
              >
                <Link
                  to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`
                    relative inline-block whitespace-nowrap px-2.5 lg:px-4 py-3 text-[13px] lg:text-sm font-semibold transition-all duration-300
                    before:content-[''] before:absolute before:inset-0 before:bg-white/10 before:backdrop-blur-sm
                    before:border before:border-white/20 before:transform before:skew-x-12
                    hover:before:bg-white/20 hover:before:border-white/30
                    ${
                      isActive(item.href)
                        ? "text-ministry-gold before:bg-ministry-gold/20 before:border-ministry-gold/30"
                        : "text-white"
                    }
                  `}
                  style={{
                    clipPath: "polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)",
                  }}
                >
                  <span className="relative z-10">{item.name}</span>
                </Link>
                {/* Slanting line divider */}
                {index < primaryItems.length - 1 && (
                  <div
                    className="absolute -right-1 top-1/2 transform -translate-y-1/2 w-px h-8 bg-white/30"
                    style={{ transform: "translateY(-50%) skewX(-12deg)" }}
                  />
                )}
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="ml-2 lg:ml-4 flex-shrink-0"
            >
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-ministry-gold text-ministry-gold hover:bg-ministry-gold hover:text-ministry-blue"
                  >
                    More
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-64 bg-ministry-blue border-ministry-gold/20">
                  <div className="flex flex-col space-y-4 mt-8">
                    {primaryItems.slice(3).map((item) => (
                      <Link
                        key={item.name}
                        to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                        onClick={() => {
                          setIsOpen(false);
                          window.scrollTo(0, 0);
                        }}
                        className={`text-sm font-medium transition-colors hover:text-ministry-gold ${
                          isActive(item.href) ? "text-ministry-gold" : "text-white"
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                    {moreItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                        onClick={() => {
                          setIsOpen(false);
                          window.scrollTo(0, 0);
                        }}
                        className={`text-sm font-medium transition-colors hover:text-ministry-gold ${
                          isActive(item.href) ? "text-ministry-gold" : "text-white"
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </motion.div>
          </motion.div>

          {/* Medium Screen Navigation (MD to LG) */}
          <div className="hidden sm:flex md:hidden items-center space-x-1 flex-shrink-0 flex-nowrap">
            {primaryItems.slice(0, 3).map((item) => (
              <Link
                key={item.name}
                to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                onClick={() => window.scrollTo(0, 0)}
                className={`px-3 py-2 text-sm font-semibold transition-all duration-300 ${
                  isActive(item.href)
                    ? "text-ministry-gold bg-white/10"
                    : "text-white hover:text-ministry-gold hover:bg-white/10"
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                  More
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-64 bg-ministry-blue border-ministry-gold/20">
                <div className="flex flex-col space-y-4 mt-8">
                  {primaryItems.slice(3).map((item) => (
                    <Link
                      key={item.name}
                      to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                      onClick={() => {
                        setIsOpen(false);
                        window.scrollTo(0, 0);
                      }}
                      className={`text-sm font-medium transition-colors hover:text-ministry-gold ${
                        isActive(item.href) ? "text-ministry-gold" : "text-white"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                  {moreItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                      onClick={() => {
                        setIsOpen(false);
                        window.scrollTo(0, 0);
                      }}
                      className={`text-sm font-medium transition-colors hover:text-ministry-gold ${
                        isActive(item.href) ? "text-ministry-gold" : "text-white"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Mobile Navigation */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="sm:hidden">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 bg-ministry-blue border-ministry-gold/20">
              <div className="flex flex-col space-y-4 mt-8">
                {primaryItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                    onClick={() => {
                      setIsOpen(false);
                      window.scrollTo(0, 0);
                    }}
                    className={`text-sm font-medium transition-colors hover:text-ministry-gold ${
                      isActive(item.href) ? "text-ministry-gold" : "text-white"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                {moreItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href} onMouseEnter={item.href === "/conference" ? prefetchConferenceHub : undefined} onFocus={item.href === "/conference" ? prefetchConferenceHub : undefined}
                    onClick={() => {
                      setIsOpen(false);
                      window.scrollTo(0, 0);
                    }}
                    className={`text-sm font-medium transition-colors hover:text-ministry-gold ${
                      isActive(item.href) ? "text-ministry-gold" : "text-white"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;
